/* Core & libraries */
import React, { useState, useEffect } from "react";
import { FormattedMessage, useIntl, defineMessages } from "react-intl";
import csvDownload from "json-to-csv-export";

/* Helpers, constants, custom methods, internal libraries */
import { useGenericApiFetch }  from "../endpoints/base";
import {
  BOOKINGS_GRID_ENDPOINT,
  MILEAGE_GRID_ENDPOINT,
  BOOKINGS_DOWNLOAD_ENDPOINT,
  MILEAGE_DOWNLOAD_ENDPOINT,
  RECEIPTS_DOWNLOAD_ENDPOINT,
  FLIGHT_SEGMENTS_GRID_ENDPOINT,
  FLIGHT_SEGMENTS_DOWNLOAD_ENDPOINT,
  GET_SUPPLIERS_BY_COMPANY_ENDPOINT,
  GET_REPORTS_TICKETS_GRID_ENDPOINT,
  DOWNLOAD_REPORTS_TICKETS_GRID_ENDPOINT,
  GET_REPORTS_COUPONS_GRID_ENDPOINT,
  DOWNLOAD_REPORTS_COUPONS_GRID_ENDPOINT,
  HOTEL_DOWNLOAD_REPORTS_ENDPOINT,
  GET_OPTIONS_ENDPOINT,
} from '../endpoints/endpoints';
import { useNotificationContext } from "../contexts/notificationContext";
import { useUserContext } from "../contexts/userContext";
import { currencyFormatWithLocale, dateFormatWithLocale, getHeadersForCsv } from '../helpers/general';
import { formatDate } from '../helpers/reportsHelper';
import { useLocaleContext } from '../contexts/localeContext';
import { AIRLINES } from "../constants/appConstants";
import { filtersMap } from '../helpers/filtersMap';


/* Components */
/* Styles */

const descriptionMessages = defineMessages({
  outOfPolicyTrue: {
    id: 'bookingsGrid.outOfPolicyTrue',
  },
  outOfPolicyFalse: {
    id: 'bookingsGrid.outOfPolicyFalse',
  },
  RC01: {
    id: 'bookingsGrid.reasonCode.RC01',
  },
  RC02: {
    id: 'bookingsGrid.reasonCode.RC02',
  },
  RC03: {
    id: 'bookingsGrid.reasonCode.RC03',
  },
  RC04: {
    id: 'bookingsGrid.reasonCode.RC04',
  },
  RC05: {
    id: 'bookingsGrid.reasonCode.RC05',
  },
  DL: {
    id: 'reports.provider.DL',
  },
  UA: {
    id: 'reports.provider.UA',
  },
  AS: {
    id: 'reports.provider.AS',
  },
  AA: {
    id: 'reports.provider.AA',
  },
});

const bookingsCsvCustomColumnsByProvider = {
  [AIRLINES.AA]: (item, intl) => ({
    ...item,
    outOfPolicy: item.outOfPolicy
      ? intl.formatMessage(descriptionMessages.outOfPolicyTrue)
      : intl.formatMessage(descriptionMessages.outOfPolicyFalse),
    reasonCode: item.reasonCode ? intl.formatMessage(descriptionMessages[item.reasonCode]) : '-',
    ticketNumber: item.ticketNumber != null ? `\u200C${item.ticketNumber}` : '',
  }),
  [AIRLINES.AS]: (item) => {
    const [bookedByFirstName = '', ...rest] = (item.bookedBy ?? '').split(' ');
    return Object.keys(item).reduce((acc, key) => {
      if (key === 'bookedBy') {
        acc.bookedByFirstName = bookedByFirstName;
        acc.bookedByLastName = rest.join(' ');
      } else if (key === 'ticketNumber') {
        acc.ticketNumber = item.ticketNumber != null ? `\u200C${item.ticketNumber}` : '';
      } else {
        acc[key] = item[key];
      }
      return acc;
    }, {});
  },
  [AIRLINES.UC]: (item, intl) => ({
    ...item,
    provider: item.provider
      && intl.formatMessage(descriptionMessages[item.provider])
  }),
};

const flightSegmentCsvCustomColumnsByProvider = {
  [AIRLINES.UC]: (item, intl) => ({
    ...item,
    provider: item.provider && intl.formatMessage(descriptionMessages[item.provider]),
  }),
};

export const useReportsService = () => {
  const [bookingsResponse, setBookingsResponse] = useState({})
  const [mileageResponse, setMileageResponse] = useState({})
  const [flightSegmentsResponse, setFlightSegmentsResponse] = useState({})
  const [ticketsResponse, setTicketsResponse] = useState({})
  const [couponsResponse, setCouponsResponse] = useState({})
  const { genericApiFetch } = useGenericApiFetch();
  const intl = useIntl();

  const { setNotification } = useNotificationContext();

  const userDetails = useUserContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;
  const globalLocale = useLocaleContext();
  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId

  useEffect(() => {
    (async () => {
      await getMileageList();
      await getBookingsList();
    })();
  }, []);

  const downloadReceipts =  async (filters) => {
    delete filters["page"];

    const response = await  genericApiFetch({
      url: RECEIPTS_DOWNLOAD_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId),
      method: "GET",
      queryParams: filters,
      spinner: "downloadReceiptsSpinner",
    })
    if (response.success) {
      csvDownload({
        data: response.data.items,
        filename: response.data.fileName,
        delimiter: ",",
        fileType: "text/csv"
      });
      return true
    } else {
      setNotification(
        "error",
        <FormattedMessage id="notifications.reportErrorTitle" />,
        <FormattedMessage id="notifications.reportErrorText" />
      )
      return false
    }
  };

  const downloadBookings = async (filters) => {
    delete filters["page"];

    const response = await  genericApiFetch({
      url: BOOKINGS_DOWNLOAD_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId),
      method: "GET",
      queryParams: filters,
      spinner: "downloadBookingsSpinner",
    })
    if (response.success) {

      //ticketNumber custom columns change applies for all providers
      const bookingCsvFormatters =
        bookingsCsvCustomColumnsByProvider[providerCode] ??
        ((item) => ({
          ...item,
          ticketNumber: item.ticketNumber != null ? `\u200C${item.ticketNumber}` : '',
        }));
      const csvConditionalContent = response.data.items.map(item => bookingCsvFormatters(item, intl));
      csvDownload({
        data: csvConditionalContent,
        filename: response.data?.fileName,
        delimiter: ",",
        fileType: "text/csv",
        headers: getHeadersForCsv(csvConditionalContent, "bookingsGrid", intl, response.data?.columns)
      });
      return true
    } else {
      setNotification(
        "error",
        <FormattedMessage id="notifications.reportErrorTitle" />,
        <FormattedMessage id="notifications.reportErrorText" />
      )
      return false
    }
  };

  const downloadMileage = async (filters) => {
    delete filters["page"];

    const response = await  genericApiFetch({
      url: MILEAGE_DOWNLOAD_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId),
      method: "GET",
      queryParams: filters,
      spinner: "downloadMileageSpinner",
    })
    if (response.success) {
      csvDownload({
        data: response.data.items,
        filename: response.data.fileName,
        delimiter: ",",
        fileType: "text/csv"
      });
      return true
    } else {
      setNotification(
        "error",
        <FormattedMessage id="notifications.reportErrorTitle" />,
        <FormattedMessage id="notifications.reportErrorText" />
      )
      return false
    }
  };

  const downloadFlightSegments = async (filters) => {
    delete filters["page"];

    const response = await genericApiFetch({
      url: FLIGHT_SEGMENTS_DOWNLOAD_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId),
      method: "GET",
      queryParams: filters,
      spinner: "downloadFlightSegmentsSpinner",
    });
    if (response.success) {
      // custom  provider columns changes
      const flightSegmentCsvFormatters =
        flightSegmentCsvCustomColumnsByProvider[providerCode] ??
        ((item) => ({
          ...item
        }));
      const csvConditionalContent = response.data.items.map((item) =>
        flightSegmentCsvFormatters(item, intl),
      );
      csvDownload({
        data: csvConditionalContent,
        filename: response.data.fileName,
        delimiter: ',',
        fileType: 'text/csv',
        headers: getHeadersForCsv(
          response.data.items,
          'flightSegmentsGrid',
          intl,
          response.data?.columns,
        ),
      });
      return true;
    } else {
      setNotification(
        "error",
        <FormattedMessage id="notifications.reportErrorTitle" />,
        <FormattedMessage id="notifications.reportErrorText" />
      );
      return false;
    }
  };

  const getFlightSegmentsList = async (options) => {
    if (options) {
      const response = await genericApiFetch({
        url: FLIGHT_SEGMENTS_GRID_ENDPOINT.replace("{companyEntityId}", companyEntityId),
        method: "GET",
        queryParams: options,
        spinner: "flightSegmentsGridSpinner"
      });

      if (response.success) {
        setFlightSegmentsResponse(response.data);
      } else {
        setFlightSegmentsResponse({ results: null });
      }
    }
  };

  const getBookingsList = async (options) => {
    if (options) {
      let filters = "";
      if (options.filters) {
        for (const [key, value] of Object.entries(options.filters)) {
          value.map(item => {
            if (item) {
              filters += `${filters ? "," : ""}${key}-${item}`
            }
          })
        }
      }
      options = {
        ...options,
        filters
      }

      const response = await genericApiFetch({
        url: BOOKINGS_GRID_ENDPOINT.replace("{companyEntityId}", companyEntityId),
        method: "GET",
        queryParams: options,
        spinner: "bookingsGridSpinner"
      });

      if (response.success) {
        setBookingsResponse(response.data);
      } else {
        setBookingsResponse({ results: null });
      }
    }
  };


  const getMileageList = async (options) => {
    if (options) {
      let filters = "";
      if (options.filters) {
        for (const [key, value] of Object.entries(options.filters)) {
          value.map(item => {
            if (item) {
              filters += `${filters ? "," : ""}${key}-${item}`
            }
          })
        }
      }
      options = {
        ...options,
        filters
      }

      const response = await genericApiFetch({
        url: MILEAGE_GRID_ENDPOINT.replace("{companyEntityId}", companyEntityId),
        method: "GET",
        queryParams: options,
        spinner: "mileageGridSpinner"
      });

      if (response.success) {
        setMileageResponse(response.data);
      } else {
        setMileageResponse({ results: null });
      }
    }
  };

  const getSuppliersByCompany = async () => {
    const response = await genericApiFetch({
      url: GET_SUPPLIERS_BY_COMPANY_ENDPOINT.replace("{companyEntityId}", companyEntityId),
      method: "GET",
    });

    if (response.success) {
      return response.data;
    }
    return false;
  };

  const getTicketsList = async (options) => {
    if (options) {
     options = filtersMap(options);
     const response = await genericApiFetch({
      url: GET_REPORTS_TICKETS_GRID_ENDPOINT.replace("{companyEntityId}", companyEntityId),
      method: "GET",
      queryParams: options,
      spinner: "ticketsGridSpinner"
     });

      if (response.success) {
        setTicketsResponse(response.data);
       } else {
        setTicketsResponse({ results: null });
      }
    }
  };
  const getCouponsList = async (options) => {
    if (options) {
      options = filtersMap(options);
      const response = await genericApiFetch({
        url: GET_REPORTS_COUPONS_GRID_ENDPOINT.replace("{companyEntityId}", companyEntityId),
        method: "GET",
        queryParams: options,
        spinner: "couponsGridSpinner"
      });

      if (response.success) {
        setCouponsResponse(response.data);
      } else {
        setCouponsResponse({ results: null });
      }
    }
  };

  const ticketsCsvFormatter = (item) => ({
    ...item,
    ticketStatus: item.ticketStatus != null
      ? intl.formatMessage({ id: `ticketsGrid.ticketStatus${item.ticketStatus}`, defaultMessage: String(item.ticketStatus) })
      : '-',
    issueDateTime: formatDate(item.issueDateTime, globalLocale),
    bookedDateTime: formatDate(item.bookedDateTime, globalLocale),
    ticketNumber: item.ticketNumber != null ? `\u200C${item.ticketNumber}` : '',
  });

  const downloadTicketsReport = async (filters) => {
    delete filters["page"];
    filters = filtersMap(filters);

    const response = await genericApiFetch({
      url: DOWNLOAD_REPORTS_TICKETS_GRID_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()
      ).replace("{companyEntityId}", companyEntityId),
      method: "GET",
      queryParams: filters,
      spinner: "ticketsReportDownloadSpinner",
    });
    if (response.success) {
      csvDownload({
        data: response.data.items.map(item => ticketsCsvFormatter(item)),
        filename: response.data?.fileName,
        delimiter: ",",
        fileType: "text/csv",
        headers: getHeadersForCsv(response.data.items, "ticketsGrid", intl)
      });
      return true
    }
    else {
      setNotification(
        "error",
        <FormattedMessage id="notifications.reportErrorTitle" />,
        <FormattedMessage id="notifications.reportErrorText" />
      )
      return false
    }
  };

  const couponsCsvFormatter = (item) => ({
    ...item,
    couponStatus: item.couponStatus != null
      ? intl.formatMessage({ id: `couponsGrid.couponStatus${item.couponStatus}`, defaultMessage: String(item.couponStatus) })
      : '-',
    issueDateTime: formatDate(item.issueDateTime, globalLocale),
    departureDateLocal: formatDate(item.departureDateLocal, globalLocale),
    arrivalDateLocal: formatDate(item.arrivalDateLocal, globalLocale),
    ticketStatus: item.ticketStatus != null
      ? intl.formatMessage({ id: `ticketsGrid.ticketStatus${item.ticketStatus}`, defaultMessage: String(item.ticketStatus) })
      : '-',
    ticketNumber: item.ticketNumber != null ? `\u200C${item.ticketNumber}` : '',
  });

  const downloadCouponsReport = async (filters) => {
    delete filters["page"];
    filters = filtersMap(filters);

    const response = await genericApiFetch({
      url: DOWNLOAD_REPORTS_COUPONS_GRID_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()
      ).replace("{companyEntityId}", companyEntityId),
      method: "GET",
      queryParams: filters,
      spinner: "ticketsReportDownloadSpinner",
    });
    if (response.success) {
      csvDownload({
        data: response.data.items.map(item => couponsCsvFormatter(item)),
        filename: response.data?.fileName,
        delimiter: ",",
        fileType: "text/csv",
        headers: getHeadersForCsv(response.data.items, "couponsGrid", intl)
      });
      return true
    }
    else {
      setNotification(
        "error",
        <FormattedMessage id="notifications.reportErrorTitle" />,
        <FormattedMessage id="notifications.reportErrorText" />
      )
      return false
    }
  };

  const getTicketStatusOptions = async (options) => {
    const response = await genericApiFetch({
      url: GET_OPTIONS_ENDPOINT,
      queryParams: { optionName: options },
      method: 'GET',
    });

    if (response.success) {
      return response.data;
    }
    setNotification(
      'error',
      <FormattedMessage id="notifications.getTicketStatusOptionsErrorTitle" />,
      <FormattedMessage id="notifications.getTicketStatusOptionsErrorText" />,
    );
    return false;
  };

  return {
    downloadBookings,
    downloadReceipts,
    downloadMileage,
    downloadFlightSegments,
    bookingsResponse,
    getBookingsList,
    mileageResponse,
    getMileageList,
    flightSegmentsResponse,
    getFlightSegmentsList,
    getSuppliersByCompany,
    getTicketsList,
    ticketsResponse,
    getCouponsList,
    couponsResponse,
    downloadTicketsReport,
    downloadCouponsReport,
    getTicketStatusOptions,
  };
}

